/**
 * Derived computations (slice F, the cheap half): observed set and drift, and
 * course and speed made good. Both compare two positions over a time interval,
 * so they fall out of the graph almost for free once the points exist.
 *
 *   - Set and drift: compare a DR with an observed fix for the same interval.
 *     The set is the direction the tide actually ran (DR toward fix), the drift
 *     is how far, and the rate is drift over time. It is the inverse of the EP
 *     construction, and a strong teaching object: it shows the tide the boat
 *     really felt, not the tide we predicted.
 *   - Course and speed made good: between two positions over an interval, the
 *     made-good course and speed over the ground. Useful for comparing what we
 *     steered against what we actually achieved.
 *
 * The maths is plain rhumb bearing and distance from the tested nav module.
 */
import { rhumbBearingDeg, rhumbDistanceNm } from '@/nav';
import type { PositionPoint, Vector, TimeStamp } from './objects';

export interface SetDrift {
  /** Direction the tide ran TOWARDS, degrees true (DR -> fix). */
  setDeg: number;
  /** Distance the tide carried us, nm. */
  driftNm: number;
  /** Rate of the tide, knots. */
  rateKnots: number;
}

export function computeSetDrift(
  dr: [number, number],
  fix: [number, number],
  hours: number,
): SetDrift {
  const setDeg = rhumbBearingDeg(dr, fix);
  const driftNm = rhumbDistanceNm(dr, fix);
  const rateKnots = hours > 0 ? driftNm / hours : 0;
  return { setDeg, driftNm, rateKnots };
}

export interface MadeGood {
  /** Course made good over the ground, degrees true. */
  cmgDeg: number;
  /** Speed made good over the ground, knots. */
  smgKnots: number;
  distanceNm: number;
}

export function computeMadeGood(
  from: [number, number],
  to: [number, number],
  hours: number,
): MadeGood {
  const cmgDeg = rhumbBearingDeg(from, to);
  const distanceNm = rhumbDistanceNm(from, to);
  const smgKnots = hours > 0 ? distanceNm / hours : 0;
  return { cmgDeg, smgKnots, distanceNm };
}

export interface BuildTwoPointParams {
  from: [number, number];
  to: [number, number];
  hours: number;
  time?: TimeStamp;
  newId: (prefix: string) => string;
}

export interface BuiltSetDrift {
  drPoint: PositionPoint;
  fixPoint: PositionPoint;
  vector: Vector;
  result: SetDrift;
}

/** Build the set-and-drift vector from a DR to an observed fix. */
export function buildSetDrift(params: BuildTwoPointParams): BuiltSetDrift {
  const { from, to, hours, time, newId } = params;
  const result = computeSetDrift(from, to, hours);

  const drPoint: PositionPoint = {
    id: newId('sdr'),
    kind: 'position_point',
    pointType: 'marker',
    position: from,
    time,
    label: 'DR',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };
  const fixPoint: PositionPoint = {
    id: newId('sfix'),
    kind: 'position_point',
    pointType: 'marker',
    position: to,
    time,
    label: 'Fix',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };
  const vector: Vector = {
    id: newId('setdrift'),
    kind: 'set_drift_vector',
    start: drPoint.id,
    directionDeg: result.setDeg,
    distanceNm: result.driftNm,
    label: `Set ${Math.round(result.setDeg)}° ${result.rateKnots.toFixed(1)} kn`,
    source: { method: 'manual' },
    parents: [drPoint.id, fixPoint.id],
    children: [],
    userPlaced: false,
  };
  return { drPoint, fixPoint, vector, result };
}

export interface BuiltMadeGood {
  fromPoint: PositionPoint;
  toPoint: PositionPoint;
  vector: Vector;
  result: MadeGood;
}

/** Build the made-good ground track between two positions. */
export function buildMadeGood(params: BuildTwoPointParams): BuiltMadeGood {
  const { from, to, hours, time, newId } = params;
  const result = computeMadeGood(from, to, hours);

  const fromPoint: PositionPoint = {
    id: newId('mgfrom'),
    kind: 'position_point',
    pointType: 'marker',
    position: from,
    time,
    label: 'From',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };
  const toPoint: PositionPoint = {
    id: newId('mgto'),
    kind: 'position_point',
    pointType: 'marker',
    position: to,
    time,
    label: 'To',
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };
  const vector: Vector = {
    id: newId('madegood'),
    kind: 'ground_track',
    start: fromPoint.id,
    directionDeg: result.cmgDeg,
    distanceNm: result.distanceNm,
    label: `CMG ${Math.round(result.cmgDeg)}° SMG ${result.smgKnots.toFixed(1)} kn`,
    source: { method: 'manual' },
    parents: [fromPoint.id, toPoint.id],
    children: [],
    userPlaced: false,
  };
  return { fromPoint, toPoint, vector, result };
}
